import {
  ServiceRequestFormData,
  OrganizationData,
  ServiceStatus
} from '../types';

/**
 * Filters packages based on user's form selections
 * @param organizationData - All organization data including packages and links
 * @param formData - Current form data with user selections
 * @returns Array of package IDs that match all criteria
 */
export function filterAvailablePackages(
  organizationData: OrganizationData | null,
  formData: ServiceRequestFormData
): number[] {
  if (!organizationData) return [];

  const { selectedCategoryId, selectedMakeId, selectedStatus, requestLocation } = formData;
  const {
    packages = [],
    packageCategoryLinks = [],
    packageMakeLinks = [],
    packageStatusLinks = [],
    packageLocationTypeLinks = []
  } = organizationData;

  // Need all three base selections to filter
  if (!selectedCategoryId || !selectedMakeId || !selectedStatus) {
    return [];
  }

  // If status allows 'both' but location not chosen yet, show nothing
  if (
    organizationData?.organizationStatusSettings?.find(
      s => s.status_value === selectedStatus
    )?.service_location === 'both' &&
    !requestLocation
  ) {
    return [];
  }

  // Filter by category
  const packagesForCategory = new Set(
    packageCategoryLinks
      .filter(link => link.category_id === selectedCategoryId)
      .map(link => link.package_id)
  );

  // Filter by make
  const packagesForMake = new Set(
    packageMakeLinks
      .filter(link => link.make_id === selectedMakeId)
      .map(link => link.package_id)
  );

  // Filter by status - special handling for repair_maintenance
  const packagesForStatus = new Set<number>();
  const statusesToMatch: ServiceStatus[] =
    selectedStatus === 'repair_maintenance'
      ? ['maintenance', 'repair', 'repair_maintenance']
      : [selectedStatus];

  packageStatusLinks.forEach(link => {
    if (statusesToMatch.includes(link.status_value)) {
      packagesForStatus.add(link.package_id);
    }
  });

  // Filter by location preference if applicable
  const packagesForLocation = new Set<number>();
  if (requestLocation) {
    packageLocationTypeLinks.forEach(link => {
      // Package is valid if its preference is 'both' OR matches the user's request
      if (
        link.location_preference === requestLocation ||
        link.location_preference === 'both'
      ) {
        packagesForLocation.add(link.package_id);
      }
    });
  }

  // Find packages that match all criteria
  const matchingPackageIds = packages
    .filter(pkg => {
      if (!pkg.id) return false;

      return (
        packagesForCategory.has(pkg.id) &&
        packagesForMake.has(pkg.id) &&
        packagesForStatus.has(pkg.id) &&
        (!requestLocation || packagesForLocation.has(pkg.id))
      );
    })
    .map(pkg => pkg.id!)
    .filter((id): id is number => id !== undefined);

  return matchingPackageIds;
}
